import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Crown, Sparkles, Receipt, AlertTriangle, RefreshCw, ExternalLink, CheckCircle2 } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import Header from "@/components/Header";
import { AnnualUpsellBanner, useAnnualUpsell } from "@/components/AnnualUpsell";

function StatusPill({ sub }) {
  if (!sub) return null;
  if (sub.status === "active") {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200" data-testid="status-pill-active">
        <Crown className="w-3 h-3 mr-1" /> {sub.tier?.toUpperCase()} · Active
      </Badge>
    );
  }
  if (sub.status === "trial") {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200" data-testid="status-pill-trial">
        <Sparkles className="w-3 h-3 mr-1" /> Free Trial
      </Badge>
    );
  }
  return <Badge variant="outline" data-testid="status-pill-free">Free</Badge>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function Billing() {
  const { user, refresh } = useAuth();
  const [history, setHistory] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const sub = user?.subscription;
  const { status: upsell } = useAnnualUpsell();

  const loadHistory = useCallback(() => {
    Promise.all([
      api.get("/billing/history"),
      api.get("/billing/stripe-status").catch(() => ({ data: { has_stripe_subscription: false } })),
    ])
      .then(([h, s]) => {
        setHistory(h.data.history);
        setStripeStatus(s.data);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Could not load payment history");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleOpenStripePortal = () => {
    setBusy(true);
    api.post("/billing/portal", { return_url: window.location.href })
      .then(({ data }) => { window.location.href = data.url; })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not open Stripe portal");
        setBusy(false);
      });
  };

  const handleCancel = () => {
    setBusy(true);
    api.post("/billing/cancel")
      .then(async () => {
        toast.success("Auto-renewal stopped. You keep Pro access until your period ends.");
        await refresh();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not cancel"))
      .finally(() => setBusy(false));
  };

  const handleAutoRenew = (enabled) => {
    setBusy(true);
    api.post("/billing/auto-renew", { enabled })
      .then(async () => {
        toast.success(enabled ? "Auto-renew preference saved." : "Auto-renew disabled.");
        await refresh();
      })
      .catch(() => toast.error("Could not update preference"))
      .finally(() => setBusy(false));
  };

  if (!user) return null;

  const paidHistory = history.filter((h) => h.applied || h.payment_status === "paid");

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="billing-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 mb-3">
              <CreditCard className="w-3 h-3 text-slate-600" />
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-700">Billing &amp; subscription</span>
            </div>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900">Manage your plan</h1>
            <p className="mt-2 text-slate-600">Cancel anytime · payment history · keep access for the full paid period.</p>
          </div>
          <StatusPill sub={sub} />
        </div>

        {upsell?.eligible && (
          <div className="mb-6">
            <AnnualUpsellBanner status={upsell} />
          </div>
        )}

        {/* Current plan card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-7 md:p-8" data-testid="current-plan-card">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Current plan</div>
              <div className="mt-1 font-outfit text-3xl font-bold text-slate-900">
                {sub?.status === "trial" && "Free trial"}
                {sub?.status === "active" && `${sub.tier === "team" ? "Team" : "Pro"} · ${sub.last_package?.includes("annual") ? "Annual" : "Monthly"}`}
                {sub?.status === "free" && "Free"}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-3 text-sm max-w-md">
                <div>
                  <div className="text-xs font-mono uppercase text-slate-400">Status</div>
                  <div className="text-slate-900 mt-0.5" data-testid="plan-status">{sub?.status || "free"}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-slate-400">Days left</div>
                  <div className="text-slate-900 mt-0.5 font-semibold" data-testid="plan-days-left">{sub?.days_left ?? 0}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-slate-400">Access until</div>
                  <div className="text-slate-900 mt-0.5">{fmtDate(sub?.access_until)}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-slate-400">Auto-renew</div>
                  <div className="text-slate-900 mt-0.5">{sub?.auto_renew ? "On" : "Off"}</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 min-w-[180px]">
              {stripeStatus?.has_stripe_customer ? (
                <>
                  <Button
                    onClick={handleOpenStripePortal}
                    disabled={busy}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white"
                    data-testid="btn-stripe-portal"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Manage with Stripe
                  </Button>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Update card, view invoices, or cancel — all handled by Stripe.
                  </p>
                </>
              ) : (
                <>
                  <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="btn-change-plan">
                    <Link to="/pricing">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {sub?.status === "active" ? "Change plan" : "Upgrade plan"}
                    </Link>
                  </Button>
                  {sub?.status === "active" && !sub.canceled_at && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" data-testid="btn-cancel-trigger" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-700">
                          Cancel auto-renewal
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Stop auto-renewal?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You will keep <strong>full Pro access until {fmtDate(sub?.access_until)}</strong> ({sub?.days_left} days from now).
                            After that date your account reverts to free — you won&apos;t be charged again.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="btn-cancel-back">Keep subscription</AlertDialogCancel>
                          <AlertDialogAction onClick={handleCancel} disabled={busy} data-testid="btn-cancel-confirm" className="bg-red-600 hover:bg-red-700">
                            Yes, stop auto-renewal
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              )}
              {sub?.canceled_at && (
                <div className="text-xs text-slate-500 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md" data-testid="canceled-note">
                  <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-600" />
                  Canceled {fmtDate(sub.canceled_at)} · access ends {fmtDate(sub.access_until)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment history */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-slate-500" /> Payment history
            </h2>
            <span className="text-xs font-mono text-slate-500">{history.length} {history.length === 1 ? "transaction" : "transactions"}</span>
          </div>
          {loading ? (
            <div className="text-slate-500 text-sm" data-testid="history-loading">Loading history…</div>
          ) : history.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="history-empty">
              <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No payments yet — you&apos;re on the free trial.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="history-table-wrap">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Plan</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Session</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((tx) => (
                    <tr key={tx.session_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`history-row-${tx.session_id}`}>
                      <td className="px-4 py-3 text-slate-700">{fmtDate(tx.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 capitalize">{tx.tier} · {tx.cycle}</div>
                        <div className="text-xs text-slate-500">{tx.package_id}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900">
                        ${tx.amount?.toFixed(2)} <span className="text-xs text-slate-400 uppercase">{tx.currency}</span>
                      </td>
                      <td className="px-4 py-3">
                        {tx.applied || tx.payment_status === "paid" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <CheckCircle2 className="w-3 h-3" /> Paid
                          </span>
                        ) : tx.payment_status === "initiated" || tx.status === "open" ? (
                          <span className="text-xs text-slate-500">Pending</span>
                        ) : (
                          <span className="text-xs text-slate-500">{tx.payment_status || "—"}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-slate-400">{tx.session_id?.slice(0, 14)}…</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {paidHistory.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              Need an invoice copy? Email{" "}
              <a href="mailto:billing@ctcpermits.com" className="text-slate-700 underline">billing@ctcpermits.com</a>{" "}
              with your session ID — we&apos;ll send a Stripe receipt.
            </p>
          )}
        </div>

        {/* Footer note about portal */}
        <div className="mt-12 bg-slate-50 border border-slate-200 rounded-xl p-6 text-sm text-slate-600" data-testid="portal-info">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-4 h-4 text-slate-400 mt-0.5 flex-none" />
            <div>
              <p className="text-slate-900 font-medium">About cancellation</p>
              <p className="mt-1">
                Our plans use period-grant billing — when you cancel, you keep all Pro features until your paid period ends, and we never auto-charge you again unless you explicitly resubscribe.
                Card data and refund requests are handled through{" "}
                <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="underline">Stripe</a> — your card details are never stored on our servers.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
